import React, { useState, useCallback } from 'react';
import { useEditorStore } from '../store/useEditorStore';
import { WallElement, AreaElement, ShapeElement } from '../types';
import { getMaterialConfig } from '../utils/wallMaterialConfig';
import { useTranslation } from 'react-i18next';

/**
 * 数字输入组件 - 失去焦点时才补0
 * 解决删除最后一个数字时自动补0的问题
 */
interface NumberInputProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
}

const NumberInput: React.FC<NumberInputProps> = ({
  value,
  onChange,
  min,
  max,
  step = 1,
  className = '',
  placeholder,
  disabled,
  readOnly,
}) => {
  const [localValue, setLocalValue] = useState<string>(String(value));
  const [isFocused, setIsFocused] = useState(false);

  // 当外部value变化且输入框未聚焦时，更新本地值
  React.useEffect(() => {
    if (!isFocused) {
      setLocalValue(String(value));
    }
  }, [value, isFocused]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    
    // 只有当输入是有效数字时才触发onChange
    const numValue = Number(newValue);
    if (newValue !== '' && !isNaN(numValue)) {
      onChange(numValue);
    }
  }, [onChange]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    // 失去焦点时，如果值为空或无效，恢复为0或最小值
    const numValue = Number(localValue);
    if (localValue === '' || isNaN(numValue)) {
      const defaultValue = min !== undefined ? min : 0;
      setLocalValue(String(defaultValue));
      onChange(defaultValue);
    } else {
      // 确保值在范围内
      let finalValue = numValue;
      if (min !== undefined && finalValue < min) finalValue = min;
      if (max !== undefined && finalValue > max) finalValue = max;
      setLocalValue(String(finalValue));
      if (finalValue !== numValue) {
        onChange(finalValue);
      }
    }
  }, [localValue, min, max, onChange]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  return (
    <input
      type="number"
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      min={min}
      max={max}
      step={step}
      className={className}
      placeholder={placeholder}
      disabled={disabled}
      readOnly={readOnly}
    />
  );
};

const ElementPropertiesPanel: React.FC = () => {
  const { t } = useTranslation();
  const { project, currentFloor, selectedElements, updateElement, rotateElement } = useEditorStore();

  if (selectedElements.length === 0) {
    return (
      <div className="text-center text-gray-400 text-sm py-8">
        {t('elementProps.noSelection')}
      </div>
    );
  }

  if (selectedElements.length > 1) {
    return (
      <div className="text-center text-gray-400 text-sm py-8">
        {t('elementProps.selectedCount', { n: selectedElements.length })}
      </div>
    );
  }

  // 获取选中的元素
  const elementId = selectedElements[0];
  let element = null;

  if (project && currentFloor) {
    const floor = project.floors.find((f) => f.id === currentFloor);
    if (floor) {
      for (const layer of floor.layers) {
        const found = layer.elements.find((el) => el.id === elementId);
        if (found) {
          element = found;
          break;
        }
      }
    }
  }

  if (!element) {
    return (
      <div className="text-center text-gray-400 text-sm py-8">
        {t('elementProps.notFound')}
      </div>
    );
  }

  const handleUpdate = (field: string, value: any) => {
    // 当材质变化时，同时更新厚度、颜色、透明度
    if (field === 'material' && element.type === 'wall') {
      const config = getMaterialConfig(value);
      updateElement(elementId, {
        material: value,
        thickness: config.thickness,
        color: config.color,
        opacity: config.opacity,
      });
    } else {
      updateElement(elementId, { [field]: value });
    }
  };

  return (
    <div className="space-y-4">
      {/* 基础信息 */}
      <div>
        <h3 className="font-medium text-gray-900 mb-3">{t('elementProps.basicInfo')}</h3>
        <div className="space-y-2">
          <div>
            <label className="block text-xs text-gray-600 mb-1">{t('elementProps.id')}</label>
            <div className="px-2 py-1.5 text-xs bg-gray-50 rounded border border-gray-200 font-mono">
              {element.id}
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">{t('elementProps.type')}</label>
            <div className="px-2 py-1.5 text-xs bg-gray-50 rounded border border-gray-200">
              {element.type}
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">{t('elementProps.name')}</label>
            <input
              type="text"
              value={element.name}
              onChange={(e) => handleUpdate('name', e.target.value)}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-300"
            />
          </div>
          
          {/* 旋转角度（所有元素支持） */}
          {element.type !== 'wall' && element.type !== 'door' && element.type !== 'stair' && (
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.rotation')}</label>
              <NumberInput
                value={element.rotation || 0}
                onChange={(angle) => rotateElement(elementId, angle)}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-300"
                step={1}
                placeholder={t('elementProps.rotationPlaceholder')}
              />
              <p className="text-xs text-gray-500 mt-1">{t('elementProps.rotationTip')}</p>
            </div>
          )}
          
          {/* 离地高度（所有元素支持） */}
          {element.type !== 'area' && (
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.floorHeight')}</label>
              <NumberInput
                value={(element.elevation || 0) / 10}
                onChange={(value) => handleUpdate('elevation', value * 10)}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-300"
                min={0}
                max={1000}
                step={1}
              />
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.heightTip')}</label>
            </div>
          )}
        </div>
      </div>

      {/* 墙面特定属性 */}
      {element.type === 'wall' && (
        <div>
          <h3 className="font-medium text-gray-900 mb-3">{t('elementProps.wallProps')}</h3>
          <div className="space-y-2">
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.thickness')}</label>
              <NumberInput
                value={(element as WallElement).thickness}
                onChange={(value) => handleUpdate('thickness', value)}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-300"
                min={50}
                max={1000}
                step={10}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.height3d')}</label>
              <NumberInput
                value={(element as WallElement).height}
                onChange={(value) => handleUpdate('height', value)}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-300"
                min={50}
                max={1000}
                step={25}
              />
              <div className="text-[10px] text-gray-400 mt-0.5">{t('elementProps.logicHeight')}</div>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.material')}</label>
              <select
                value={(element as WallElement).material}
                onChange={(e) => handleUpdate('material', e.target.value)}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-300"
              >
                <option value="concrete">{t('materials.concrete')}</option>
                <option value="brick">{t('materials.brick')}</option>
                <option value="wood">{t('materials.wood')}</option>
                <option value="glass">{t('materials.glass')}</option>
                <option value="metal">{t('materials.metal')}</option>
                <option value="drywall">{t('materials.drywall')}</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.color')}</label>
              <input
                type="color"
                value={(element as WallElement).color}
                onChange={(e) => handleUpdate('color', e.target.value)}
                className="w-full h-8 border border-gray-300 rounded cursor-pointer"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.opacity')}</label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={(element as WallElement).opacity ?? 1}
                onChange={(e) => handleUpdate('opacity', Number(e.target.value))}
                className="w-full"
              />
              <div className="text-xs text-gray-500 text-right">
                {Math.round(((element as WallElement).opacity ?? 1) * 100)}%
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 区域特定属性 */}
      {element.type === 'area' && (
        <div>
          <h3 className="font-medium text-gray-900 mb-3">{t('elementProps.areaProps')}</h3>
          <div className="space-y-2">
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.areaName')}</label>
              <input
                type="text"
                value={(element as AreaElement).areaName || ''}
                onChange={(e) => handleUpdate('areaName', e.target.value)}
                placeholder={t('elementProps.areaNamePlaceholder')}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-300"
              />
            </div>
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={(element as AreaElement).showName ?? true}
                  onChange={(e) => handleUpdate('showName', e.target.checked)}
                  className="rounded border-gray-300 text-blue-500 focus:ring-primary-300"
                />
                <span className="text-xs text-gray-700">{t('elementProps.showIn3d')}</span>
              </label>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.color')}</label>
              <input
                type="color"
                value={(element as AreaElement).color}
                onChange={(e) => handleUpdate('color', e.target.value)}
                className="w-full h-8 border border-gray-300 rounded cursor-pointer"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.opacity')}</label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={(element as AreaElement).fillOpacity}
                onChange={(e) => handleUpdate('fillOpacity', Number(e.target.value))}
                className="w-full"
              />
              <div className="text-xs text-gray-500 text-right">
                {Math.round(((element as AreaElement).fillOpacity) * 100)}%
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 普通矩形属性（没有furnitureName的） */}
      {element.type === 'rectangle' && !(element as ShapeElement).furnitureName && (
        <div>
          <h3 className="font-medium text-gray-900 mb-3">{t('elementProps.rectProps')}</h3>
          <div className="space-y-2">
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.height')}</label>
              <NumberInput
                value={(element as ShapeElement).height || 750}
                onChange={(value) => handleUpdate('height', value)}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-300"
                min={100}
                step={50}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.color')}</label>
              <input
                type="color"
                value={(element as ShapeElement).color || '#a5d8ff'}
                onChange={(e) => handleUpdate('color', e.target.value)}
                className="w-full h-8 border border-gray-300 rounded cursor-pointer"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.opacity')}</label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={(element as ShapeElement).opacity ?? 0.9}
                onChange={(e) => handleUpdate('opacity', Number(e.target.value))}
                className="w-full"
              />
              <div className="text-xs text-gray-500 text-right">
                {Math.round(((element as ShapeElement).opacity ?? 0.9) * 100)}%
              </div>
            </div>
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={(element as ShapeElement).passable ?? false}
                  onChange={(e) => handleUpdate('passable', e.target.checked)}
                  className="rounded border-gray-300 text-blue-500 focus:ring-primary-300"
                />
                <span className="text-xs text-gray-700">{t('elementProps.allowPassing')}</span>
              </label>
              <p className="text-xs text-gray-500 mt-1">{t('elementProps.allowPassingTip')}</p>
            </div>
          </div>
        </div>
      )}

      {/* 普通圆形属性（没有furnitureName的） */}
      {element.type === 'circle' && !(element as ShapeElement).furnitureName && (
        <div>
          <h3 className="font-medium text-gray-900 mb-3">{t('elementProps.circleProps')}</h3>
          <div className="space-y-2">
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.height')}</label>
              <NumberInput
                value={(element as ShapeElement).height || 750}
                onChange={(value) => handleUpdate('height', value)}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-300"
                min={100}
                step={50}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.color')}</label>
              <input
                type="color"
                value={(element as ShapeElement).color || '#b8ddf0'}
                onChange={(e) => handleUpdate('color', e.target.value)}
                className="w-full h-8 border border-gray-300 rounded cursor-pointer"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.opacity')}</label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={(element as ShapeElement).opacity ?? 0.9}
                onChange={(e) => handleUpdate('opacity', Number(e.target.value))}
                className="w-full"
              />
              <div className="text-xs text-gray-500 text-right">
                {Math.round(((element as ShapeElement).opacity ?? 0.9) * 100)}%
              </div>
            </div>
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={(element as ShapeElement).passable ?? false}
                  onChange={(e) => handleUpdate('passable', e.target.checked)}
                  className="rounded border-gray-300 text-blue-500 focus:ring-primary-300"
                />
                <span className="text-xs text-gray-700">{t('elementProps.allowPassing')}</span>
              </label>
              <p className="text-xs text-gray-500 mt-1">{t('elementProps.allowPassingTip')}</p>
            </div>
          </div>
        </div>
      )}

      {/* 普通多边形属性（没有furnitureName的） */}
      {element.type === 'polygon' && !(element as ShapeElement).furnitureName && (
        <div>
          <h3 className="font-medium text-gray-900 mb-3">{t('elementProps.polygonProps')}</h3>
          <div className="space-y-2">
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.height')}</label>
              <NumberInput
                value={(element as ShapeElement).height || 750}
                onChange={(value) => handleUpdate('height', value)}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-300"
                min={100}
                step={50}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.color')}</label>
              <input
                type="color"
                value={(element as ShapeElement).color || '#c3e7f5'}
                onChange={(e) => handleUpdate('color', e.target.value)}
                className="w-full h-8 border border-gray-300 rounded cursor-pointer"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.opacity')}</label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={(element as ShapeElement).opacity ?? 0.9}
                onChange={(e) => handleUpdate('opacity', Number(e.target.value))}
                className="w-full"
              />
              <div className="text-xs text-gray-500 text-right">
                {Math.round(((element as ShapeElement).opacity ?? 0.9) * 100)}%
              </div>
            </div>
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={(element as ShapeElement).passable ?? false}
                  onChange={(e) => handleUpdate('passable', e.target.checked)}
                  className="rounded border-gray-300 text-blue-500 focus:ring-primary-300"
                />
                <span className="text-xs text-gray-700">{t('elementProps.allowPassing')}</span>
              </label>
              <p className="text-xs text-gray-500 mt-1">{t('elementProps.allowPassingTip')}</p>
            </div>
          </div>
        </div>
      )}

      {/* 家具特定属性 */}
      {element.type === 'rectangle' && (element as ShapeElement).furnitureName && (
        <div>
          <h3 className="font-medium text-gray-900 mb-3">{t('elementProps.furnitureProps')}</h3>
          <div className="space-y-2">
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.furnitureName')}</label>
              <input
                type="text"
                value={(element as ShapeElement).furnitureName || ''}
                onChange={(e) => handleUpdate('furnitureName', e.target.value)}
                placeholder={t('elementProps.furnitureNamePlaceholder')}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-300"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.width')}</label>
              <NumberInput
                value={(element as ShapeElement).width || 800}
                onChange={(value) => handleUpdate('width', value)}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-300"
                min={100}
                max={5000}
                step={50}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.depth')}</label>
              <NumberInput
                value={(element as ShapeElement).depth || 600}
                onChange={(value) => handleUpdate('depth', value)}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-300"
                min={100}
                max={5000}
                step={50}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.height')}</label>
              <NumberInput
                value={(element as ShapeElement).height}
                onChange={(value) => handleUpdate('height', value)}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-300"
                min={10}
                step={10}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.color')}</label>
              <input
                type="color"
                value={(element as ShapeElement).color}
                onChange={(e) => handleUpdate('color', e.target.value)}
                className="w-full h-8 border border-gray-300 rounded cursor-pointer"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.opacity')}</label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={(element as ShapeElement).opacity ?? 0.9}
                onChange={(e) => handleUpdate('opacity', Number(e.target.value))}
                className="w-full"
              />
              <div className="text-xs text-gray-500 text-right">
                {Math.round(((element as ShapeElement).opacity ?? 0.9) * 100)}%
              </div>
            </div>
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={(element as ShapeElement).passable ?? false}
                  onChange={(e) => handleUpdate('passable', e.target.checked)}
                  className="rounded border-gray-300 text-blue-500 focus:ring-primary-300"
                />
                <span className="text-xs text-gray-700">{t('elementProps.allowPassing')}</span>
              </label>
              <p className="text-xs text-gray-500 mt-1">{t('elementProps.allowPassingModelTip')}</p>
            </div>
          </div>
        </div>
      )}

      {/* 圆形特定属性 */}
      {element.type === 'circle' && (element as ShapeElement).furnitureName && (
        <div>
          <h3 className="font-medium text-gray-900 mb-3">{t('elementProps.circleDeviceProps')}</h3>
          <div className="space-y-2">
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.deviceName')}</label>
              <input
                type="text"
                value={(element as ShapeElement).furnitureName || ''}
                onChange={(e) => handleUpdate('furnitureName', e.target.value)}
                placeholder={t('elementProps.circlePlaceholder')}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-300"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.radius')}</label>
              <NumberInput
                value={(element as ShapeElement).radius || 300}
                onChange={(value) => handleUpdate('radius', value)}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-300"
                min={50}
                max={3000}
                step={50}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.height')}</label>
              <NumberInput
                value={(element as ShapeElement).height}
                onChange={(value) => handleUpdate('height', value)}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-300"
                min={10}
                step={10}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.color')}</label>
              <input
                type="color"
                value={(element as ShapeElement).color}
                onChange={(e) => handleUpdate('color', e.target.value)}
                className="w-full h-8 border border-gray-300 rounded cursor-pointer"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.opacity')}</label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={(element as ShapeElement).opacity ?? 0.9}
                onChange={(e) => handleUpdate('opacity', Number(e.target.value))}
                className="w-full"
              />
              <div className="text-xs text-gray-500 text-right">
                {Math.round(((element as ShapeElement).opacity ?? 0.9) * 100)}%
              </div>
            </div>
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={(element as ShapeElement).passable ?? false}
                  onChange={(e) => handleUpdate('passable', e.target.checked)}
                  className="rounded border-gray-300 text-blue-500 focus:ring-primary-300"
                />
                <span className="text-xs text-gray-700">{t('elementProps.allowPassing')}</span>
              </label>
              <p className="text-xs text-gray-500 mt-1">{t('elementProps.allowPassingModelTip')}</p>
            </div>
          </div>
        </div>
      )}

      {/* 多边形特定属性 */}
      {element.type === 'polygon' && (element as ShapeElement).furnitureName && (
        <div>
          <h3 className="font-medium text-gray-900 mb-3">{t('elementProps.polygonDeviceProps')}</h3>
          <div className="space-y-2">
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.deviceName')}</label>
              <input
                type="text"
                value={(element as ShapeElement).furnitureName || ''}
                onChange={(e) => handleUpdate('furnitureName', e.target.value)}
                placeholder={t('elementProps.polygonPlaceholder')}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-300"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.height')}</label>
              <NumberInput
                value={(element as ShapeElement).height}
                onChange={(value) => handleUpdate('height', value)}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-300"
                min={10}
                step={10}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.color')}</label>
              <input
                type="color"
                value={(element as ShapeElement).color}
                onChange={(e) => handleUpdate('color', e.target.value)}
                className="w-full h-8 border border-gray-300 rounded cursor-pointer"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.opacity')}</label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={(element as ShapeElement).opacity ?? 0.9}
                onChange={(e) => handleUpdate('opacity', Number(e.target.value))}
                className="w-full"
              />
              <div className="text-xs text-gray-500 text-right">
                {Math.round(((element as ShapeElement).opacity ?? 0.9) * 100)}%
              </div>
            </div>
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={(element as ShapeElement).passable ?? false}
                  onChange={(e) => handleUpdate('passable', e.target.checked)}
                  className="rounded border-gray-300 text-blue-500 focus:ring-primary-300"
                />
                <span className="text-xs text-gray-700">{t('elementProps.allowPassing')}</span>
              </label>
              <p className="text-xs text-gray-500 mt-1">{t('elementProps.allowPassingModelTip')}</p>
            </div>
          </div>
        </div>
      )}

      {/* 文本特定属性 */}
      {element.type === 'text' && (
        <div>
          <h3 className="font-medium text-gray-900 mb-3">{t('elementProps.textProps')}</h3>
          <div className="space-y-2">
            {/* Content */}
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.textContent')}</label>
              <textarea
                value={(element as any).content}
                onChange={(e) => handleUpdate('content', e.target.value)}
                rows={3}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-300"
              />
            </div>
            
            {/* Font Size */}
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.fontSize')}</label>
              <NumberInput
                value={(element as any).fontSize}
                onChange={(value) => handleUpdate('fontSize', value)}
                min={8}
                max={72}
                step={1}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-300"
              />
            </div>
            
            {/* Text Color */}
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.textColor')}</label>
              <input
                type="color"
                value={(element as any).color}
                onChange={(e) => handleUpdate('color', e.target.value)}
                className="w-full h-8 border border-gray-300 rounded cursor-pointer"
              />
            </div>
            
            {/* Border Color */}
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.borderColor')}</label>
              <input
                type="color"
                value={(element as any).borderColor}
                onChange={(e) => handleUpdate('borderColor', e.target.value)}
                className="w-full h-8 border border-gray-300 rounded cursor-pointer"
              />
            </div>
            
            {/* Background Color */}
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.bgColor')}</label>
              <input
                type="color"
                value={(element as any).backgroundColor}
                onChange={(e) => handleUpdate('backgroundColor', e.target.value)}
                className="w-full h-8 border border-gray-300 rounded cursor-pointer"
              />
            </div>
            
            {/* Background Opacity */}
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.bgOpacity')}</label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={(element as any).backgroundOpacity}
                onChange={(e) => handleUpdate('backgroundOpacity', Number(e.target.value))}
                className="w-full"
              />
              <div className="text-xs text-gray-500 text-right">
                {Math.round(((element as any).backgroundOpacity) * 100)}%
              </div>
            </div>
            
            {/* Font Family */}
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.font')}</label>
              <select
                value={(element as any).fontFamily}
                onChange={(e) => handleUpdate('fontFamily', e.target.value)}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-300"
              >
                <option value="sans-serif">Sans-serif</option>
                <option value="serif">Serif</option>
                <option value="monospace">Monospace</option>
                <option value="Arial">Arial</option>
                <option value="Microsoft YaHei">{t('elementProps.msyh')}</option>
              </select>
            </div>
            
            {/* Bold & Italic */}
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={(element as any).bold}
                  onChange={(e) => handleUpdate('bold', e.target.checked)}
                  className="rounded border-gray-300 text-blue-500 focus:ring-primary-300"
                />
                <span className="text-xs text-gray-700">{t('elementProps.bold')}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={(element as any).italic}
                  onChange={(e) => handleUpdate('italic', e.target.checked)}
                  className="rounded border-gray-300 text-blue-500 focus:ring-primary-300"
                />
                <span className="text-xs text-gray-700">{t('elementProps.italic')}</span>
              </label>
            </div>
            
            {/* Alignment */}
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.align')}</label>
              <select
                value={(element as any).align}
                onChange={(e) => handleUpdate('align', e.target.value)}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-300"
              >
                <option value="left">{t('elementProps.alignLeft')}</option>
                <option value="center">{t('elementProps.alignCenter')}</option>
                <option value="right">{t('elementProps.alignRight')}</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* 3D模型特定属性 */}
      {element.type === 'model3d' && (
        <div>
          <h3 className="font-medium text-gray-900 mb-3">{t('elementProps.model3dProps')}</h3>
          <div className="space-y-2">
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.modelName')}</label>
              <div className="px-2 py-1.5 text-xs bg-gray-50 rounded border border-gray-200">
                {(element as any).modelName}
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.category')}</label>
              <div className="px-2 py-1.5 text-xs bg-gray-50 rounded border border-gray-200">
                {(element as any).modelCategory}
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.widthCm')}</label>
              <input
                type="number"
                value={(() => {
                  const el = element as any;
                  // 如果有realWidth，使用realWidth
                  if (el.realWidth) {
                    return el.realWidth / 10;
                  }
                  // 否则从width反推
                  // 自动校准：world坐标直接是cm
                  // 手动校准：需要用calibrationScale转换
                  const isAutoCalibrated = project?.mapConfig?.autoCalibrated || false;
                  if (isAutoCalibrated) {
                    return el.width || 50; // autoCalibrated时width直接是cm
                  }
                  const calibrationScale = project?.mapConfig?.calibrationScale || 10;
                  return (el.width / calibrationScale) || 50;
                })()}
                onChange={(e) => {
                  const newWidthCm = Number(e.target.value);
                  const newWidth = newWidthCm * 10; // cm → mm
                  const el = element as any;
                  const isAutoCalibrated = project?.mapConfig?.autoCalibrated || false;
                  const calibrationScale = project?.mapConfig?.calibrationScale || 10;
                  
                  // 获取旧的尺寸，如果realWidth看起来不合理（< 1000mm），从width反推
                  let oldWidth = el.realWidth;
                  let oldDepth = el.realDepth;
                  
                  if (!oldWidth || oldWidth < 1000) {
                    // realWidth缺失或太小（可能是旧数据），从width反推
                    if (isAutoCalibrated) {
                      oldWidth = (el.width || 50) * 10; // autoCalibrated: width直接是cm
                    } else {
                      oldWidth = (el.width / calibrationScale) * 10; // px → cm → mm
                    }
                    console.warn('⚠️ realWidth异常，从width反推:', oldWidth, 'mm');
                  }
                  
                  if (!oldDepth || oldDepth < 1000) {
                    // realDepth缺失或太小（可能是旧数据），从depth反推
                    if (isAutoCalibrated) {
                      oldDepth = (el.depth || 50) * 10; // autoCalibrated: depth直接是cm
                    } else {
                      oldDepth = (el.depth / calibrationScale) * 10; // px → cm → mm
                    }
                    console.warn('⚠️ realDepth异常，从depth反推:', oldDepth, 'mm');
                  }
                  
                  // 计算比例
                  const ratio = newWidth / oldWidth;
                  const newDepth = oldDepth * ratio;

                  // 计算新的像素尺寸（用于渲染和位置）
                  // 自动校准：world坐标直接是cm，canvasWidth = cm值
                  // 手动校准：canvasWidth = cm * calibrationScale
                  let newWidthPx: number;
                  let newDepthPx: number;
                  
                  if (isAutoCalibrated) {
                    newWidthPx = newWidthCm; // 直接使用cm作为世界坐标
                    newDepthPx = newDepth / 10; // mm → cm
                  } else {
                    newWidthPx = (newWidth / 10) * calibrationScale; // mm → cm → px
                    newDepthPx = (newDepth / 10) * calibrationScale; // mm → cm → px
                  }

                  // 计算中心点（从旧的points中获取）
                  const oldPoints = (el as any).points || [];
                  const centerX = oldPoints.length > 0
                    ? oldPoints.reduce((sum: number, p: { x: number }) => sum + p.x, 0) / oldPoints.length
                    : 0;
                  const centerY = oldPoints.length > 0
                    ? oldPoints.reduce((sum: number, p: { y: number }) => sum + p.y, 0) / oldPoints.length
                    : 0;

                  console.log(`📐 [ElementPropertiesPanel] 元素: ${el.name || el.id}`);
                  console.log(`📐 [ElementPropertiesPanel] 当前旋转角度: ${el.rotation || 0}°`);
                  console.log(`📐 [ElementPropertiesPanel] 中心点: (${centerX.toFixed(2)}, ${centerY.toFixed(2)})`);
                  console.log(`📐 [ElementPropertiesPanel] 当前points:`, oldPoints.map((p: { x: number, y: number }, i: number) => `[${i}](${p.x.toFixed(1)}, ${p.y.toFixed(1)})`));
                  console.log(`📐 [ElementPropertiesPanel] 新尺寸: ${newWidthPx.toFixed(2)} x ${newDepthPx.toFixed(2)}`);

                  // 获取当前旋转角度（弧度）
                  const rotationRad = ((el as any).rotation || 0) * Math.PI / 180;
                  const cos = Math.cos(rotationRad);
                  const sin = Math.sin(rotationRad);
                  console.log(`📐 [ElementPropertiesPanel] 旋转弧度: ${rotationRad.toFixed(4)}, cos: ${cos.toFixed(4)}, sin: ${sin.toFixed(4)}`);

                  // 计算新的角点（考虑旋转角度，保持中心点不变）
                  const halfWidth = newWidthPx / 2;
                  const halfDepth = newDepthPx / 2;

                  // 相对于中心的偏移量
                  const offsets = [
                    { dx: -halfWidth, dy: -halfDepth },  // 左上
                    { dx: halfWidth, dy: -halfDepth },   // 右上
                    { dx: halfWidth, dy: halfDepth },    // 右下
                    { dx: -halfWidth, dy: halfDepth },   // 左下
                  ];

                  // 应用旋转公式计算新角点
                  const newPoints = offsets.map((offset, i) => {
                    const newX = centerX + (offset.dx * cos - offset.dy * sin);
                    const newY = centerY + (offset.dx * sin + offset.dy * cos);
                    console.log(`📐 [ElementPropertiesPanel] 角点${i}: offset(${offset.dx.toFixed(1)}, ${offset.dy.toFixed(1)}) → (${newX.toFixed(1)}, ${newY.toFixed(1)})`);
                    return { x: newX, y: newY };
                  });

                  // 验证新中心点
                  const newCenterX = newPoints.reduce((sum: number, p: { x: number }) => sum + p.x, 0) / newPoints.length;
                  const newCenterY = newPoints.reduce((sum: number, p: { y: number }) => sum + p.y, 0) / newPoints.length;
                  console.log(`📐 [ElementPropertiesPanel] 新中心点: (${newCenterX.toFixed(2)}, ${newCenterY.toFixed(2)})`);
                  console.log(`📐 [ElementPropertiesPanel] 中心点偏移: (${(newCenterX - centerX).toFixed(4)}, ${(newCenterY - centerY).toFixed(4)})`);

                  console.log('📝 修改Model3D宽度:', {
                    input: newWidthCm + 'cm',
                    oldSize: { width: oldWidth, depth: oldDepth, unit: 'mm' },
                    newSize: { width: newWidth, depth: newDepth, unit: 'mm' },
                    newPixels: { width: newWidthPx, depth: newDepthPx },
                    ratio: ratio.toFixed(4)
                  });

                  // 一次性更新所有尺寸相关属性
                  updateElement(element.id, {
                    realWidth: newWidth,
                    realDepth: newDepth,
                    width: newWidthPx,
                    depth: newDepthPx,
                    points: newPoints,
                  });
                }}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-300"
                min="10"
                max="500"
                step="5"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.depthCm')}</label>
              <input
                type="number"
                value={(() => {
                  const el = element as any;
                  // 如果有realDepth，使用realDepth
                  if (el.realDepth) {
                    return el.realDepth / 10;
                  }
                  // 否则从depth反推（假设使用了校准比例）
                  const calibrationScale = project?.mapConfig?.calibrationScale || 10;
                  return (el.depth / calibrationScale) || 50;
                })()}
                readOnly
                disabled
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded bg-gray-100 text-gray-500 cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 mt-1">{t('elementProps.depthTip')}</p>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.heightCm')}</label>
              <NumberInput
                value={((element as any).height / 10) || 75}
                onChange={(value) => handleUpdate('height', value * 10)}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-300"
                min={10}
                max={300}
                step={5}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.scale')}</label>
              <NumberInput
                value={(element as any).scale || 1}
                onChange={(value) => handleUpdate('scale', value)}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-300"
                min={0.1}
                max={10}
                step={0.1}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.pitch')}</label>
              <NumberInput
                value={(element as any).rotationX || 0}
                onChange={(value) => handleUpdate('rotationX', value)}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-300"
                min={-180}
                max={180}
                step={5}
              />
              <p className="text-xs text-gray-500 mt-1">{t('elementProps.pitchTip')}</p>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('elementProps.modelColor')}</label>
              <input
                type="color"
                value={(element as any).color || '#888888'}
                onChange={(e) => handleUpdate('color', e.target.value)}
                className="w-full h-8 border border-gray-300 rounded cursor-pointer"
              />
              <p className="text-xs text-gray-500 mt-1">{t('elementProps.modelColorTip')}</p>
            </div>
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={(element as any).passable ?? false}
                  onChange={(e) => handleUpdate('passable', e.target.checked)}
                  className="rounded border-gray-300 text-blue-500 focus:ring-primary-300"
                />
                <span className="text-xs text-gray-700">{t('elementProps.allowPassing')}</span>
              </label>
              <p className="text-xs text-gray-500 mt-1">{t('elementProps.allowPassingModelTip')}</p>
            </div>
          </div>
        </div>
      )}

      {/* 端点信息 */}
      <div>
        <h3 className="font-medium text-gray-900 mb-3">{t('elementProps.endpointCoords')}</h3>
        <div className="space-y-1 max-h-40 overflow-y-auto">
          {element.points.map((point, index) => (
            <div key={index} className="flex items-center gap-2 text-xs">
              <span className="text-gray-600 w-12">{t('elementProps.point', { n: index + 1 })}</span>
              <span className="font-mono bg-gray-50 px-2 py-1 rounded border border-gray-200">
                X: {Math.round(point.x)}, Y: {Math.round(point.y)}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* 可见性控制 */}
      <div>
        <h3 className="font-medium text-gray-900 mb-3">{t('elementProps.displaySettings')}</h3>
        <div className="space-y-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={element.visible}
              onChange={(e) => handleUpdate('visible', e.target.checked)}
              className="rounded border-gray-300 text-blue-500 focus:ring-primary-300"
            />
            <span className="text-xs text-gray-700">{t('elementProps.visible')}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={element.locked}
              onChange={(e) => handleUpdate('locked', e.target.checked)}
              className="rounded border-gray-300 text-blue-500 focus:ring-primary-300"
            />
            <span className="text-xs text-gray-700">{t('elementProps.locked')}</span>
          </label>
        </div>
      </div>
    </div>
  );
};

export default ElementPropertiesPanel;
